import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { GRID_SIZE } from '../lib/constants';
import { getPieceBackgroundPosition } from '../lib/gameUtils';

const TouchDragContext = createContext(null);

export function useTouchDrag() {
  return useContext(TouchDragContext);
}

export function TouchDragProvider({ children, onDrop, onSwap, onReturnToTray, pieceSize }) {
  const [draggingPiece, setDraggingPiece] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  
  const startDrag = useCallback((piece, fromTray, sourceRow, sourceCol, startX, startY) => {
    setDraggingPiece({ piece, fromTray, sourceRow, sourceCol });
    setDragPosition({ x: startX, y: startY });
    setIsDragging(true);
    dragStartRef.current = { x: startX, y: startY };
  }, []);
  
  const updateDrag = useCallback((x, y) => {
    if (isDragging) {
      setDragPosition({ x, y });
    }
  }, [isDragging]);
  
  const endDrag = useCallback((endX, endY) => {
    if (!isDragging || !draggingPiece) {
      setIsDragging(false);
      setDraggingPiece(null);
      return;
    }
    
    // Find the element at the drop position
    const elements = document.elementsFromPoint(endX, endY);
    const boardCell = elements.find(el => el.classList.contains('board-cell'));
    const piecesTray = elements.find(el => el.classList.contains('pieces-tray'));
    
    const { piece, fromTray, sourceRow, sourceCol } = draggingPiece;
    
    // Check if dropped on pieces tray (return piece to tray)
    if (piecesTray && !fromTray && onReturnToTray) {
      onReturnToTray(sourceRow, sourceCol);
      setIsDragging(false);
      setDraggingPiece(null);
      return;
    }
    
    if (boardCell) {
      const row = parseInt(boardCell.dataset.row, 10);
      const col = parseInt(boardCell.dataset.col, 10);
      
      if (!isNaN(row) && !isNaN(col)) {
        // Check if there's already a piece in the target cell
        const hasPiece = boardCell.querySelector('.puzzle-piece') !== null;
        const targetPieceCorrect = boardCell.dataset.correct === 'true';
        
        if (hasPiece && !targetPieceCorrect) {
          // Swap pieces
          onSwap(piece.id, sourceRow, sourceCol, row, col, fromTray);
        } else if (!hasPiece) {
          // Place piece
          onDrop(piece.id, row, col, fromTray, sourceRow, sourceCol);
        }
      }
    }
    
    setIsDragging(false);
    setDraggingPiece(null);
  }, [isDragging, draggingPiece, onDrop, onSwap, onReturnToTray]);
  
  const cancelDrag = useCallback(() => {
    setIsDragging(false);
    setDraggingPiece(null);
  }, []);
  
  return (
    <TouchDragContext.Provider value={{ startDrag, updateDrag, endDrag, cancelDrag, isDragging, draggingPiece }}>
      {children}
      {/* Touch drag preview */}
      {isDragging && draggingPiece && (
        <TouchDragPreview 
          piece={draggingPiece.piece} 
          pieceSize={pieceSize} 
          position={dragPosition} 
        />
      )}
    </TouchDragContext.Provider>
  );
}

function TouchDragPreview({ piece, pieceSize, position }) {
  const totalSize = pieceSize * GRID_SIZE;
  const backgroundPosition = getPieceBackgroundPosition(piece, pieceSize);
  
  return (
    <div
      className="fixed pointer-events-none z-[10000]"
      style={{
        left: position.x - pieceSize / 2,
        top: position.y - pieceSize / 2,
      }}
    >
      <div
        className="rounded-sm shadow-xl bg-no-repeat"
        style={{
          width: pieceSize,
          height: pieceSize,
          backgroundImage: `url(${piece.imageUrl})`,
          backgroundPosition,
          backgroundSize: `${totalSize}px ${totalSize}px`,
          transform: `rotate(${piece.rotation}deg) scale(1.1)`,
          border: '3px solid hsl(var(--primary))',
          opacity: 0.95,
          // Lighten dark areas to show more detail (match PuzzlePiece)
          filter: 'brightness(1.1) contrast(1.05)',
        }}
      />
    </div>
  );
}
